/**
 * Whether a failed background re-read may keep what is on screen.
 *
 * A quiet re-read (after a change in the editor, a list or tree refreshing behind it) keeps its data only
 * when the failure says nothing about access: the read was cancelled, never reached the server, was
 * throttled (429), or the server or a dependency failed (any 5xx, including both 503 codes,
 * `permission_service_unreachable` and `sphere_read_unavailable`). Everything else, 401, 403, 404, another
 * status, or an error this cannot read, takes the view's normal failure path, so a person whose access was
 * just removed is never shown data the next full read would not show (spec D71, frontend `permissions.md`
 * Rule 6: fail closed).
 */

const CANCEL_NAMES: ReadonlySet<string> = new Set(['CanceledError', 'AbortError']);
const CANCEL_CODES: ReadonlySet<string> = new Set(['ERR_CANCELED']);
/** axios' codes for a request that got no response: the network failed or the request timed out. */
const NETWORK_CODES: ReadonlySet<string> = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT']);

interface FailureLike {
  name?: unknown;
  code?: unknown;
  status?: unknown;
  response?: { status?: unknown } | null;
}

export function isTransientReadFailure(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const failure = err as FailureLike;

  if (typeof failure.name === 'string' && CANCEL_NAMES.has(failure.name)) return true;
  if (typeof failure.code === 'string' && CANCEL_CODES.has(failure.code)) return true;

  const status = failure.response?.status ?? failure.status;
  if (status !== undefined) {
    return typeof status === 'number' && (status === 429 || (status >= 500 && status <= 599));
  }

  return typeof failure.code === 'string' && NETWORK_CODES.has(failure.code);
}
